import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { LocalStorageKeys } from '@shared/constants/local-storage-keys';
import { RouteNetwork } from '../interfaces/map-data';
import { GetRowsRequest, RestApiService } from '@core/services/rest-api.service';
import { map, of } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class RouteNetworkFavoritesService {
  private readonly localStorageKey = LocalStorageKeys.RouteNetworkFavorites;
  private readonly api = inject(RestApiService);

  private readonly initialIds = this.load();
  private readonly favoriteIds = signal<number[]>(this.initialIds);

  // Favorites added after app load (full objects available immediately from toggle caller)
  private readonly sessionFavorites = signal<RouteNetwork[]>([]);

  private readonly favoritesResource = rxResource({
    stream: () => {
      if (!this.initialIds.length) return of([]);

      const payload: GetRowsRequest = {
        entity: 'RouteNetworkWeb',
        fields: [
          'RouteNetworkID',
          'Name',
          'Description',
          'IsActive',
          'CustomerID',
          'Association',
          'CountyName',
          'Latitude',
          'Longitude',
          'LastMaintainedTimeUTC',
        ],
        filterModel: {
          RouteNetworkID: {
            filterType: 'set',
            values: this.initialIds,
          },
          IsActive: {
            filter: 'true',
            filterType: 'text',
            type: 'equals',
          },
        },
      };

      return this.api.getRows<RouteNetwork>(payload).pipe(map((resp) => resp.data));
    },
  });

  readonly favorites = computed(() => this.favoriteIds());
  readonly isLoading = this.favoritesResource.isLoading;

  // Merged view: resolved from API + added during session, minus any removed
  readonly resolvedFavorites = computed<RouteNetwork[]>(() => {
    const ids = new Set(this.favoriteIds());
    const resolved = this.favoritesResource.value() ?? [];
    const session = this.sessionFavorites();

    const merged = new Map<number, RouteNetwork>();

    for (const rn of [...resolved, ...session]) {
      merged.set(rn.RouteNetworkID!, rn);
    }

    // Filter out any that were removed via toggle
    return Array.from(merged.values()).filter((rn) => ids.has(rn.RouteNetworkID!));
  });

  constructor() {
    effect(() => {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.favoriteIds()));
    });
  }

  isFavorite(id: number): boolean {
    return this.favoriteIds().includes(id);
  }

  toggle(routeNetwork: RouteNetwork): void {
    const id = routeNetwork.RouteNetworkID!;
    const current = this.favoriteIds();
    const isRemoving = current.includes(id);

    this.favoriteIds.set(isRemoving ? current.filter((favId) => favId !== id) : [...current, id]);

    if (!isRemoving) {
      this.sessionFavorites.update((prev) => [...prev, routeNetwork]);
    }
  }

  private load(): number[] {
    const raw = localStorage.getItem(this.localStorageKey);
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return this.migrate(parsed);
    } catch {
      return [];
    }
  }

  private migrate(parsed: unknown): number[] {
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is number => typeof item === 'number');
    }

    if (parsed !== null && typeof parsed === 'object') {
      return Object.keys(parsed)
        .map(Number)
        .filter((id) => !isNaN(id));
    }

    return [];
  }
}
